import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/solicitacao.css'; // Podemos reaproveitar a grid e estilo da solicitação ou criar solucaoinstitucional.css
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const SolucaoInstitucionalManager = () => {
    const navigate = useNavigate();
    const [dados, setDados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });

    const [formData, setFormData] = useState({
        processo_sei: '',
        id_escola: '',
        id_modalidade: '',
        id_tipo_servico: '',
        objeto: '',
        valor_investimento: '',
        data_inicio: '',
        data_final: ''
    });

    const [apiStatus, setApiStatus] = useState('checking');
    const [lastUpdate, setLastUpdate] = useState(null);

    const [escolas, setEscolas] = useState([]);
    const [escolaSearch, setEscolaSearch] = useState('');
    const [modalidades, setModalidades] = useState([]);
    const [tiposServico, setTiposServico] = useState([]);

    const tabulatorRef = useRef(null);
    const tableRef = useRef(null);

    const escolasFiltradas = useMemo(() => {
        if (!escolaSearch) return escolas;
        return escolas.filter(escola =>
            escola.nome_escola?.toLowerCase().includes(escolaSearch.toLowerCase()) ||
            escola.id_escola?.toString().includes(escolaSearch) ||
            escola.codigo_inep?.toString().includes(escolaSearch)
        );
    }, [escolas, escolaSearch]);

    const mostrarMensagem = useCallback((tipo, texto) => {
        setMensagem({ tipo, texto });
        setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
    }, []);

    const fetchDataDependencies = useCallback(async () => {
        try {
            const [escolasRes, modRes, servRes] = await Promise.all([
                api.get('/escola'),
                api.get('/tipo-modalidade'),
                api.get('/tipo-servico')
            ]);
            setEscolas(escolasRes.data.data || []);
            setModalidades(modRes.data.data || []);
            setTiposServico(servRes.data.data || []);
        } catch (error) {
            mostrarMensagem('error', `Erro ao carregar dependências: ${error.message}`);
        }
    }, [mostrarMensagem]);

    const verificarAPI = useCallback(async () => {
        try {
            setApiStatus('checking');
            await api.get('/solucao-institucional');
            setApiStatus('connected');
        } catch (error) {
            setApiStatus('error');
            if (error.response && error.response.status === 403) {
                mostrarMensagem('error', 'Você não tem permissão para acessar este recurso.');
            } else {
                mostrarMensagem('error', `Erro de conexão: ${error.message}`);
            }
        }
    }, [mostrarMensagem]);

    const carregarDados = useCallback(async (showMessage = true) => {
        try {
            setLoading(true);
            const response = await api.get('/solucao-institucional');
            setDados(response.data.data || []);
            setLastUpdate(new Date());
            if (showMessage) {
                mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
            }
        } catch (error) {
            mostrarMensagem('error', error.message);
            setDados([]);
        } finally {
            setLoading(false);
        }
    }, [mostrarMensagem]);

    useEffect(() => {
        verificarAPI();
        fetchDataDependencies();
    }, [verificarAPI, fetchDataDependencies]);

    useEffect(() => {
        if (apiStatus === 'connected') {
            carregarDados(false);
        }
    }, [apiStatus, carregarDados]);

    const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
        logarRequisicao('Atualizar', 'solucao_institucional', { id, dadosAtualizacao });
        try {
            const dadosValidos = { ...dadosAtualizacao };
            if (dadosValidos.hasOwnProperty('valor_investimento')) {
                const valorString = String(dadosValidos.valor_investimento);
                if (valorString === '' || dadosValidos.valor_investimento === null || valorString === 'null') {
                    dadosValidos.valor_investimento = null;
                } else if (valorString.includes(',')) {
                    // Valor formatado brasileiro (ex: 1.000,00)
                    dadosValidos.valor_investimento = parseFloat(valorString.replace(/\./g, '').replace(',', '.'));
                } else {
                    // Valor simples
                    dadosValidos.valor_investimento = parseFloat(dadosValidos.valor_investimento);
                }
            }

            const response = await api.put(`/solucao-institucional/${id}`, dadosValidos);
            logarSucesso('Atualizar', 'solucao_institucional', response.data);
            if (response && response.data) {
                if (tabulatorRef.current) {
                    tabulatorRef.current.updateData([response.data.data]);
                }
                mostrarMensagem('success', 'Registro atualizado com sucesso!');
                carregarDados(false);
            } else {
                mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
            }
        } catch (error) {
            const mensagemErro = tratarErroAPI(error, 'atualizar', 'solução institucional');
            mostrarMensagem('error', mensagemErro);
        }
    }, [mostrarMensagem, carregarDados]);

    const excluirRegistro = useCallback(async (id) => {
        logarRequisicao('Excluir', 'solucao_institucional', { id });
        try {
            const response = await api.delete(`/solucao-institucional/${id}`);
            logarSucesso('Excluir', 'solucao_institucional', response.data);
            setDados(currentData => currentData.filter(item => item.id_solucao !== id));
            mostrarMensagem('success', 'Registro excluído com sucesso!');
        } catch (error) {
            const mensagemErro = tratarErroAPI(error, 'excluir', 'solução institucional');
            mostrarMensagem('error', mensagemErro);
        }
    }, [mostrarMensagem]);

    const colunas = useMemo(() => {
        const escolasNomes = escolas.reduce((acc, item) => ({ ...acc, [item.id_escola]: item.nome_escola }), {});
        const modNomes = modalidades.reduce((acc, item) => ({ ...acc, [item.id_tp_modalidade]: item.descr_modalidade }), {});
        const servNomes = tiposServico.reduce((acc, item) => ({ ...acc, [item.id]: item.descricao }), {});

        const dateEditor = (cell, onRendered, success, cancel) => {
            const input = document.createElement("input");
            input.setAttribute("type", "text");
            input.setAttribute("placeholder", "DD/MM/YYYY");
            input.style.padding = "4px";
            input.style.width = "100%";
            input.style.boxSizing = "border-box";

            const value = cell.getValue();
            if (value) {
                try {
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                        const day = String(date.getUTCDate()).padStart(2, '0');
                        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                        const year = date.getUTCFullYear();
                        input.value = `${day}/${month}/${year}`;
                    } else {
                        input.value = value;
                    }
                } catch (e) {
                    input.value = value;
                }
            }

            onRendered(() => {
                input.focus();
                input.style.height = "100%";
            });

            const handleDateInput = (e) => {
                const input = e.target;
                let value = input.value.replace(/\D/g, '');
                let formattedValue = '';
                if (value.length > 0) {
                    formattedValue = value.substring(0, 2);
                }
                if (value.length > 2) {
                    formattedValue += '/' + value.substring(2, 4);
                }
                if (value.length > 4) {
                    formattedValue += '/' + value.substring(4, 8);
                }
                input.value = formattedValue;
            };

            input.addEventListener('input', handleDateInput);

            const onChange = () => {
                if (input.value && input.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                    const [day, month, year] = input.value.split('/');
                    success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
                } else {
                    success(input.value);
                }
            };

            input.addEventListener("change", onChange);
            input.addEventListener("blur", onChange);

            return input;
        };

        const dateFormatter = (cell) => {
            const value = cell.getValue();
            if (!value) return '';
            try {
                const date = new Date(value);
                if (isNaN(date.getTime())) return value;
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                return `${day}/${month}/${year}`;
            } catch (e) {
                return value;
            }
        };

        return [
            {
                title: 'Salvar',
                field: 'salvar',
                width: 80,
                hozAlign: 'center',
                formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
                cellClick: (e, cell) => {
                    const data = cell.getRow().getData();
                    atualizarRegistro(data.id_solucao, data);
                },
                headerSort: false
            },
            {
                title: 'Excluir',
                field: 'excluir',
                width: 80,
                hozAlign: 'center',
                formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
                cellClick: (e, cell) => {
                    const data = cell.getRow().getData();
                    if (window.confirm(`Deseja realmente excluir o registro "${data.id_solucao}" ? `)) {
                        excluirRegistro(data.id_solucao);
                    }
                },
                headerSort: false
            },
            {
                title: 'Processo SEI',
                field: 'processo_sei',
                width: 180,
                headerFilter: 'input',
                editor: 'input'
            },
            {
                title: 'Escola',
                field: 'id_escola',
                width: 250,
                headerFilter: 'input',
                formatter: (cell) => escolasNomes[cell.getValue()] || cell.getValue(),
                editor: 'list',
                editorParams: { values: escolasNomes }
            },
            {
                title: 'Modalidade',
                field: 'id_modalidade',
                width: 180,
                headerFilter: 'input',
                formatter: (cell) => modNomes[cell.getValue()] || cell.getValue(),
                editor: 'list',
                editorParams: { values: modNomes }
            },
            {
                title: 'Tipo serviço',
                field: 'id_tipo_servico',
                width: 180,
                headerFilter: 'input',
                formatter: (cell) => servNomes[cell.getValue()] || cell.getValue(),
                editor: 'list',
                editorParams: { values: servNomes }
            },
            {
                title: 'Objeto',
                field: 'objeto',
                width: 500,
                headerFilter: 'input',
                editor: 'textarea',
                editorParams: {
                    elementAttributes: {
                        maxlength: "500"
                    },
                    verticalNavigation: "editor"
                },
                formatter: (cell) => {
                    const value = cell.getValue();
                    return value ? `<div style="white-space: normal; overflow: visible;">${value}</div>` : '';
                }
            },
            {
                title: 'Valor investimento',
                field: 'valor_investimento',
                width: 200,
                headerFilter: 'input',
                sorter: 'number',
                formatter: (cell) => {
                    const value = cell.getValue();
                    if (value === null || value === undefined || value === '') {
                        return '';
                    }
                    return parseFloat(value).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                    });
                },
                headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
                    if (!headerValue) return true;
                    if (rowValue === null || rowValue === undefined || rowValue === '') return false;

                    const formattedValue = parseFloat(rowValue).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                    });
                    const searchValue = headerValue.trim();

                    if (formattedValue.includes(searchValue)) return true;

                    const numericValue = rowValue.toString();
                    if (numericValue.includes(searchValue.replace(',', '.'))) return true;

                    const cleanFormatted = formattedValue.replace(/[^\d,]/g, '').replace(/\./g, '').replace(',', '.');
                    const cleanSearch = searchValue.replace(/[^\d,]/g, '').replace(/\./g, '').replace(',', '.');

                    return cleanFormatted.includes(cleanSearch);
                },
                editor: function (cell, onRendered, success, cancel) {
                    const input = document.createElement('input');
                    input.type = 'text';
                    input.style.width = '100%';
                    input.style.padding = '4px';
                    input.style.boxSizing = 'border-box';

                    const currentValue = cell.getValue();
                    if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
                        input.value = parseFloat(currentValue).toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        });
                    }

                    input.addEventListener('input', (e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value) {
                            value = (parseInt(value) / 100).toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            });
                            e.target.value = value;
                        }
                    });

                    onRendered(() => {
                        input.focus();
                        input.select();
                    });

                    const successFunc = () => {
                        // Handle empty/cleared string properly
                        if (!input.value) {
                            success(null);
                            return;
                        }
                        const rawValue = input.value.replace(/\./g, '').replace(',', '.');
                        success(parseFloat(rawValue) || 0);
                    };

                    input.addEventListener('blur', successFunc);
                    input.addEventListener('keydown', (e) => {
                        if (e.key === 'Enter') {
                            successFunc();
                        }
                        if (e.key === 'Escape') {
                            cancel();
                        }
                    });

                    return input;
                }
            },
            {
                title: 'Data início',
                field: 'data_inicio',
                width: 150,
                formatter: dateFormatter,
                editor: dateEditor
            },
            {
                title: 'Data final',
                field: 'data_final',
                width: 150,
                formatter: dateFormatter,
                editor: dateEditor
            }
        ];
    }, [escolas, modalidades, tiposServico, atualizarRegistro, excluirRegistro]);

    const inicializarTabulator = useCallback(() => {
        if (!tableRef.current) return;
        if (tabulatorRef.current) tabulatorRef.current.destroy();

        tabulatorRef.current = new Tabulator(tableRef.current, {
            data: dados,
            columns: colunas,
            layout: 'fitColumns',
            pagination: 'local',
            paginationSize: 10,
            paginationCounter: 'rows',
            movableColumns: true,
            resizableRows: true,
            tooltips: true,
            placeholder: 'Nenhum registro encontrado',
            index: 'id_solucao'
        });

        const style = document.createElement('style');
        style.textContent = `
      .btn-save { background: #007bff; color: white; border: none; padding: 5px 10px; border-radius: 3px; cursor: pointer; font-size: 12px; }
      .btn-save:hover { background: #0056b3; }
      .btn-delete { background: #dc3545; color: white; border: none; padding: 5px 10px; border-radius: 3px; cursor: pointer; font-size: 12px; }
      .btn-delete:hover { background: #c82333; }
      .btn-back { background: transparent; border: none; font-size: 24px; cursor: pointer; }
    `;
        document.head.appendChild(style);
    }, [dados, colunas]);

    useEffect(() => {
        if (apiStatus === 'connected' && tableRef.current) {
            if (!tabulatorRef.current) {
                inicializarTabulator();
            } else {
                tabulatorRef.current.setColumns(colunas);
                tabulatorRef.current.setData(dados);
            }
        }
    }, [dados, apiStatus, inicializarTabulator, colunas]);

    const adicionarRegistro = async (e) => {
        e.preventDefault();
        const { processo_sei, id_escola, id_modalidade, id_tipo_servico, objeto, valor_investimento, data_inicio, data_final } = formData;

        const camposFaltando = [];
        if (!id_escola) camposFaltando.push('Escola');
        if (!id_modalidade) camposFaltando.push('Modalidade');
        if (!id_tipo_servico) camposFaltando.push('Tipo Serviço');

        if (camposFaltando.length > 0) {
            mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposFaltando.join(', ')} `);
            return;
        }

        if (data_inicio && data_final && data_final < data_inicio) {
            mostrarMensagem('error', 'A Data Final não pode ser menor que a Data Início.');
            return;
        }

        const dataToSend = {
            processo_sei,
            id_escola: id_escola || null,
            id_modalidade,
            id_tipo_servico: parseInt(id_tipo_servico, 10),
            objeto,
            valor_investimento: valor_investimento === '' || valor_investimento == null ? null : parseFloat(String(valor_investimento).replace(/\./g, '').replace(',', '.')),
            data_inicio: data_inicio === '' ? null : data_inicio,
            data_final: data_final === '' ? null : data_final
        };

        logarRequisicao('Adicionar', 'solucao_institucional', dataToSend);
        try {
            setLoading(true);
            const response = await api.post('/solucao-institucional', dataToSend);
            logarSucesso('Adicionar', 'solucao_institucional', response.data);
            setDados([response.data.data, ...dados]);
            setFormData({
                processo_sei: '', id_escola: '', id_modalidade: '', id_tipo_servico: '',
                objeto: '', valor_investimento: '', data_inicio: '', data_final: ''
            });
            setEscolaSearch('');
            mostrarMensagem('success', 'Registro adicionado com sucesso!');
        } catch (error) {
            const mensagemErro = tratarErroAPI(error, 'adicionar', 'solução institucional');
            mostrarMensagem('error', mensagemErro);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'valor_investimento') {
            const apenasNumeros = value.replace(/\D/g, '');
            if (apenasNumeros === '') {
                setFormData(prev => ({ ...prev, [name]: '' }));
                return;
            }
            const numero = parseInt(apenasNumeros, 10);
            const valorFormatado = (numero / 100).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            setFormData(prev => ({ ...prev, [name]: valorFormatado }));
        } else {
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    const exportarDados = () => {
        if (tabulatorRef.current) {
            tabulatorRef.current.download('csv', 'solucao_institucional.csv');
        }
    };

    return (
        <div className="tipo-ambiente-container">
            <div className="header-controls">
                <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
            </div>

            <div className="header">
                <div className="title-container">
                    <img src={etgLogo} alt="ETG Logo" className="logo" />
                    <h1>Gerenciador de Solução Institucional</h1>
                    <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
                </div>
                <p>Interface para popular e gerenciar soluções institucionais</p>
            </div>

            {mensagem.texto && (
                <div className="status-container">
                    <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
                </div>
            )}

            <div className="controls">
                <form onSubmit={adicionarRegistro} className="form-inline">
                    <div className="form-group">
                        <label>Processo SEI:</label>
                        <input type="text" name="processo_sei" value={formData.processo_sei} onChange={handleInputChange} disabled={loading} />
                    </div>
                    <div className="form-group">
                        <label>Escola:<span style={{ color: 'red' }}>*</span></label>
                        <input
                            type="text"
                            value={escolaSearch}
                            onChange={(e) => {
                                setEscolaSearch(e.target.value);
                                const esc = escolas.find(x => x.nome_escola === e.target.value || x.id_escola === e.target.value);
                                setFormData(prev => ({ ...prev, id_escola: esc ? esc.id_escola : '' }));
                            }}
                            list="esc-list-solucao"
                            placeholder="Buscar escola..."
                            disabled={loading}
                            autoComplete="off"
                        />
                        <datalist id="esc-list-solucao">
                            {escolasFiltradas.map(item => (
                                <option key={item.id_escola} value={item.nome_escola} />
                            ))}
                        </datalist>
                    </div>
                    <div className="form-group">
                        <label>Modalidade:<span style={{ color: 'red' }}>*</span></label>
                        <select name="id_modalidade" value={formData.id_modalidade} onChange={handleInputChange} disabled={loading}>
                            <option value="">Selecione...</option>
                            {modalidades.map(item => (<option key={item.id_tp_modalidade} value={item.id_tp_modalidade}>{item.descr_modalidade}</option>))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Tipo serviço:<span style={{ color: 'red' }}>*</span></label>
                        <select name="id_tipo_servico" value={formData.id_tipo_servico} onChange={handleInputChange} disabled={loading}>
                            <option value="">Selecione...</option>
                            {tiposServico.map(item => (<option key={item.id} value={item.id}>{item.descricao}</option>))}
                        </select>
                    </div>
                    <div className="form-group" style={{ flex: '1 1 100%', width: '100%' }}>
                        <label>Objeto:</label>
                        <textarea 
                            name="objeto" 
                            value={formData.objeto} 
                            onChange={handleInputChange} 
                            disabled={loading}
                            rows="3"
                            maxLength="500"
                            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', minHeight: '60px' }}
                            placeholder="Descreva o objeto..."
                        />
                    </div>
                    <div className="form-group">
                        <label>Valor investimento:</label>
                        <input type="text" name="valor_investimento" value={formData.valor_investimento} onChange={handleInputChange} placeholder="Ex: 1.000,00" disabled={loading} />
                    </div>
                    <div className="form-group">
                        <label>Data início:</label>
                        <input type="date" name="data_inicio" value={formData.data_inicio} onChange={handleInputChange} disabled={loading} />
                    </div>
                    <div className="form-group">
                        <label>Data final:</label>
                        <input type="date" name="data_final" value={formData.data_final} onChange={handleInputChange} disabled={loading} />
                    </div>

                    <div className="actions-row" style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
                        <button type="submit" className="btn btn-success" disabled={loading}>
                            {loading ? '⏳...' : '➕ Adicionar'}
                        </button>
                        <button type="button" className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading}>
                            🔄 Recarregar
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>
                            📥 Exportar
                        </button>
                        <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
                            <i className="fas fa-info-circle"></i>
                            <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
                        </div>
                    </div>
                </form>
            </div>

            <div className="stats-bar">
                <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
                <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
            </div>

            <div className="tabulator-container">
                <div ref={tableRef}></div>
            </div>
        </div >
    );
};

export default SolucaoInstitucionalManager;
